import { useApolloClient } from '@apollo/client';
import React, { createContext, useState, useContext, useEffect } from 'react';

import {
  ListMessagesDocument,
  useCreateMessageReactionMutation,
} from '@graphql/generated';
import useActiveChat from '@hooks/useActiveChat';
import useMe from '@hooks/useMe';

type PendingReaction = { messageId: string; emoji: string };

type EmojiContext = {
  emoji: string;
  from: string;
  isEmojiPickerOpened: boolean;
  showMessageHighlightModal: boolean;
  showMessageEmojiModal: boolean;
  messageId: string;
  emojiPositionY: number;
  pendingReaction: PendingReaction | null;
  setEmojiPositionY: React.Dispatch<React.SetStateAction<number>>;
  setEmojiValue: React.Dispatch<React.SetStateAction<string>>;
  setFromValue: React.Dispatch<React.SetStateAction<string>>;
  toggleEmojiPickerOpened: React.Dispatch<React.SetStateAction<boolean>>;
  setShowMessageHighlightModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowMessageEmojiModal: React.Dispatch<React.SetStateAction<boolean>>;
  setMessageId: React.Dispatch<React.SetStateAction<string>>;
};

const emojiContext = createContext<EmojiContext | undefined>(undefined);

export const EmojiProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { Provider } = emojiContext;
  const [emoji, setEmojiValue] = useState('');
  const [from, setFromValue] = useState('');
  const [isEmojiPickerOpened, toggleEmojiPickerOpened] = useState(false);
  const [emojiPositionY, setEmojiPositionY] = useState(0);
  const [showMessageHighlightModal, setShowMessageHighlightModal] =
    useState(false);
  const [showMessageEmojiModal, setShowMessageEmojiModal] = useState(false);
  const [messageId, setMessageId] = useState('');
  const [pendingReaction, setPendingReaction] =
    useState<PendingReaction | null>(null);

  // activeChatId is only populated from sessionStorage on page-reload (web).
  // The main web open-chat path calls setActiveChat but never setActiveChatId,
  // so activeChat?.id is the canonical source for the current chat (TA-10151).
  const { activeChatId, activeChat } = useActiveChat();
  const chatId = activeChat?.id ?? activeChatId;

  const apolloClient = useApolloClient();
  const [createMessageReactionMutation] = useCreateMessageReactionMutation();
  const { me } = useMe();

  useEffect(() => {
    if (emoji !== '' && from === 'MessageReaction') {
      const tempId = `optimistic-${messageId}-${emoji}`;
      const meUser = me?.id ? { __typename: 'User' as const, id: me.id } : null;

      // Signal ChatMessage to render the badge immediately, before the Apollo
      // pipeline propagates. Cleared once the server-confirmed reaction lands
      // in the message's reaction prop (see ChatMessage.web.tsx effectiveReaction).
      setPendingReaction({ messageId, emoji });

      // Snapshot the pre-write reaction array so onError can restore it exactly.
      let preWriteReactions: any[] | null = null;

      // Write optimistic stub into the cache; the mutation's update callback
      // replaces it with the authoritative server entry on completion.
      if (chatId) {
        apolloClient.cache.updateQuery(
          {
            query: ListMessagesDocument,
            variables: { chatId },
            returnPartialData: true,
          },
          (data: any) => {
            if (!data?.listMessages?.edges) return data;
            const updatedEdges = (data.listMessages.edges as any[]).map(
              (edge) => {
                if (!edge?.node || edge.node.id !== messageId) return edge;
                preWriteReactions = edge.node.reaction ?? null;
                const existingReaction = (
                  (edge.node.reaction ?? []) as any[]
                ).find((r) => r?.reaction === emoji);
                if (existingReaction) {
                  return {
                    ...edge,
                    node: {
                      ...edge.node,
                      reaction: ((edge.node.reaction ?? []) as any[]).map(
                        (r) =>
                          r?.reaction === emoji
                            ? {
                                ...r,
                                count: r.count + 1,
                                // Add me to the user list so MessageEmojiModal's
                                // user-check routes correctly to remove on immediate tap.
                                user: [
                                  ...((r.user ?? []) as any[]).filter(
                                    (u: any) => u?.id !== me?.id
                                  ),
                                  ...(meUser ? [meUser] : []),
                                ],
                              }
                            : r
                      ),
                    },
                  };
                }
                return {
                  ...edge,
                  node: {
                    ...edge.node,
                    reaction: [
                      ...((edge.node.reaction ?? []) as any[]),
                      {
                        __typename: 'MessageReaction',
                        id: tempId,
                        reaction: emoji,
                        count: 1,
                        reactionId: tempId,
                        user: meUser ? [meUser] : [],
                      },
                    ],
                  },
                };
              }
            );
            return {
              ...data,
              listMessages: { ...data.listMessages, edges: updatedEdges },
            };
          }
        );
      }

      createMessageReactionMutation({
        variables: { messageId, reaction: emoji },
        update(cache, { data }) {
          // Replace the temp stub with the authoritative server entry so the
          // cache doesn't carry a dangling optimistic-* id after the mutation.
          const confirmed = data?.createMessageReaction;
          if (!confirmed || !chatId) return;
          cache.updateQuery(
            {
              query: ListMessagesDocument,
              variables: { chatId },
              returnPartialData: true,
            },
            (existing: any) => {
              if (!existing?.listMessages?.edges) return existing;
              const edges = (existing.listMessages.edges as any[]).map(
                (edge) => {
                  if (!edge?.node || edge.node.id !== messageId) return edge;
                  return {
                    ...edge,
                    node: {
                      ...edge.node,
                      reaction: ((edge.node.reaction ?? []) as any[]).map(
                        (r) =>
                          r?.id === tempId
                            ? {
                                ...r,
                                id: confirmed.id,
                                reactionId:
                                  confirmed.reactionId ?? confirmed.id,
                                count: confirmed.count,
                              }
                            : r
                      ),
                    },
                  };
                }
              );
              return {
                ...existing,
                listMessages: { ...existing.listMessages, edges },
              };
            }
          );
        },
        onError() {
          // Restore the pre-write snapshot so a failed mutation doesn't leave a
          // phantom reaction in the cache.
          setPendingReaction(null);
          if (!chatId || preWriteReactions === null) return;
          apolloClient.cache.updateQuery(
            {
              query: ListMessagesDocument,
              variables: { chatId },
              returnPartialData: true,
            },
            (existing: any) => {
              if (!existing?.listMessages?.edges) return existing;
              const edges = (existing.listMessages.edges as any[]).map(
                (edge) => {
                  if (!edge?.node || edge.node.id !== messageId) return edge;
                  return {
                    ...edge,
                    node: { ...edge.node, reaction: preWriteReactions },
                  };
                }
              );
              return {
                ...existing,
                listMessages: { ...existing.listMessages, edges },
              };
            }
          );
        },
        onCompleted() {
          setEmojiValue('');
        },
      });
    }
  }, [emoji, messageId]);

  // Safety valve: clear stale pendingReaction if the server never confirms.
  useEffect(() => {
    if (!pendingReaction) return;
    const t = setTimeout(() => setPendingReaction(null), 8000);
    return () => clearTimeout(t);
  }, [pendingReaction]);

  useEffect(() => {
    if (!isEmojiPickerOpened) {
      setFromValue('');
      setEmojiValue('');
    }
  }, [isEmojiPickerOpened]);

  return (
    <Provider
      value={{
        emoji,
        from,
        isEmojiPickerOpened,
        messageId,
        setEmojiValue,
        setFromValue,
        toggleEmojiPickerOpened,
        showMessageHighlightModal,
        setShowMessageHighlightModal,
        showMessageEmojiModal,
        setShowMessageEmojiModal,
        setMessageId,
        emojiPositionY,
        setEmojiPositionY,
        pendingReaction,
      }}>
      {children}
    </Provider>
  );
};

const useEmoji = (): EmojiContext => {
  const context = useContext(emojiContext);
  if (context === undefined) {
    throw new Error('useEmoji must be used within a Provider');
  }

  return context;
};

export default useEmoji;
